import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createItem, updateItem, fetchItemById } from './itemSlice';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import log from '../../logger';
import ReactForm from '../../components/ReactForm';
import ReactDropdown from '../../components/ReactDropdown';
import { paths } from '../../routes/helpers';
import api from '../../api/requests';
import { getImageUrl, toLocalUploadPath } from '../../utils/urlUtils';
import ImageGallery from '../../components/ImageGallery';

const ItemCreateEdit = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { itemId } = useParams();
  const { loading = false, error = null, items = [] } = useSelector((state) => state.item || {});
  const item = itemId ? items.find(i => i._id === itemId) : null;

  const [imageFiles, setImageFiles] = useState([]);
  const [businessOptions, setBusinessOptions] = useState([]);
  const [tryWithItemsOptions, setTryWithItemsOptions] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedTryWithItems, setSelectedTryWithItems] = useState([]);
  const [existingImages, setExistingImages] = useState([]);

  useEffect(() => {
    if (itemId) {
      dispatch(fetchItemById(itemId))
        .unwrap()
        .catch(error => {
          toast.error('Failed to load item data');
          log.error('Error fetching item:', error);
          navigate(paths.private.item.list());
        });
    }
  }, [itemId, dispatch, navigate]);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [businessResponse, itemsResponse] = await Promise.all([
          api.businesses.getAll(),
          api.items.getAll()
        ]);

        const businesses = businessResponse.data.data.businesses.map(business => ({
          value: business._id,
          label: business.title
        }));
        setBusinessOptions(businesses);

        const items = itemsResponse.data.data.items.map(item => ({
          value: item._id,
          label: item.title
        }));
        setTryWithItemsOptions(items);
      } catch (error) {
        console.error('Error fetching options:', error);
        toast.error('Failed to load dropdown options');
      }
    };

    fetchOptions();
  }, []);

  useEffect(() => {
    if (item && businessOptions.length > 0 && tryWithItemsOptions.length > 0) {
      if (item.images && Array.isArray(item.images)) {
        const formattedImages = item.images.map(img => getImageUrl(img));
        setExistingImages(formattedImages);
      }
      
      const business = businessOptions.find(b => b.value === item.businessId);
      setSelectedBusiness(business);

      if (item.tryWithItems && Array.isArray(item.tryWithItems)) {
      const selectedItems = (item.tryWithItems || [])
        .map(itemId => tryWithItemsOptions.find(option => option.value === itemId))
        .filter(Boolean);
        setSelectedTryWithItems(selectedItems);
      }
    }
  }, [item, businessOptions, tryWithItemsOptions]);

  const handleRemoveExistingImage = (imageUrl) => {
    setExistingImages(prev => prev.filter(url => url !== imageUrl));
  };

  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    setImageFiles(prevFiles => [...prevFiles, ...files]);
  };

  const handleRemoveImage = (index) => {
    setImageFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const handleSubmit = async (formData) => {
    try {
      if (!selectedBusiness && fields.find(f => f.name === 'businessId')?.required) {
        toast.error('Business is required');
        return;
      }

      const formDataToSubmit = new FormData();

      Object.keys(formData).forEach(key => {
        if (formData[key] !== undefined && 
            formData[key] !== null && 
            key !== 'businessId' && 
            key !== 'tryWithItems' &&
            key !== 'images') {
          formDataToSubmit.append(key, formData[key]);
        }
      });

      if (imageFiles.length === 0) {
        existingImages.forEach(imageUrl => {
          formDataToSubmit.append('existingImages', toLocalUploadPath(imageUrl));
        });
      } else {
        imageFiles.forEach((file) => {
          formDataToSubmit.append('images', file);
        });
      }

      if (selectedBusiness?.value) {
        formDataToSubmit.append('businessId', selectedBusiness.value.toString());
      }
      
      if (selectedTryWithItems?.length > 0) {
        selectedTryWithItems.forEach(item => {
          if (item?.value) {
            formDataToSubmit.append('tryWithItems', item.value.toString());
          }
        });
      }

      if (itemId) {
        await dispatch(updateItem({ id: itemId, itemData: formDataToSubmit })).unwrap();
        toast.success('Item updated successfully!');
        log.info('Item updated:', formData.title);
      } else {
        await dispatch(createItem(formDataToSubmit)).unwrap();
        toast.success('Item created successfully!');
        log.info('Item created:', formData.title);
      }

      navigate(paths.private.item.list());
    } catch (err) {
      toast.error(err.message || 'An error occurred');
      log.error('Error creating/updating item:', err);
    }
  };

  const fields = [
    {
      name: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Enter item title'
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      required: true,
      placeholder: 'Enter item description'
    },
    {
      name: 'category',
      label: 'Category',
      type: 'text',
      required: true,
      placeholder: 'Enter item category'
    },
    {
      name: 'minPrice',
      label: 'Minimum Price',
      type: 'number',
      required: true,
      placeholder: 'Enter minimum price'
    },
    {
      name: 'maxPrice',
      label: 'Maximum Price',
      type: 'number',
      required: true,
      placeholder: 'Enter maximum price'
    },
    {
      name: 'images',
      label: 'Images',
      type: 'file',
      accept: 'image/*',
      multiple: true,
      required: !itemId && existingImages.length === 0,
      onChange: handleImageChange,
      preview: (
        <ImageGallery 
          existingImages={imageFiles.length > 0 ? [] : existingImages}
          imageFiles={imageFiles}
          onRemoveExisting={handleRemoveExistingImage}
          onRemoveNew={handleRemoveImage}
          loading={loading && item?.images?.length > 0 && existingImages.length === 0}
        />
      )
    },
    {
      name: 'rating',
      label: 'Rating',
      type: 'number',
      required: false,
      placeholder: 'Enter item rating (0-5)',
      min: 0,
      max: 5,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      options: [
        { value: 0, label: 'Inactive' },
        { value: 1, label: 'Active' },
      ],
    },
    {
      name: 'businessId',
      label: 'Business',
      type: 'dropdown',
      required: true,
      component: (
        <ReactDropdown
          options={businessOptions}
          value={selectedBusiness}
          onChange={(selected) => {
            setSelectedBusiness(selected);
          }}
          required={true}
          error={!selectedBusiness ? 'Business is required' : null}
        />
      )
    },
    {
      name: 'tryWithItems',
      label: 'Try With Items',
      type: 'dropdown',
      component: (
        <ReactDropdown
          options={tryWithItemsOptions}
          value={selectedTryWithItems}
          onChange={setSelectedTryWithItems}
          isMulti={true}
        />
      )
    },
  ];

  const defaultValues = item ? {
    title: item.title,
    description: item.description,
    category: item.category,
    minPrice: item.minPrice,
    maxPrice: item.maxPrice,
    rating: item.rating || 0,
    status: item.status,
    businessId: item.businessId,
    tryWithItems: item.tryWithItems,
  } : {
    title: '',
    description: '',
    category: '',
    minPrice: 0,
    maxPrice: 0,
    rating: 0,
    status: 1,
    businessId: '',
    tryWithItems: [],
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">
        {itemId ? 'Edit Item' : 'Create Item'}
      </h2>
      <ReactForm
        fields={fields}
        onSubmit={handleSubmit}
        loading={loading}
        error={error}
        defaultValues={defaultValues}
        submitText={itemId ? 'Update Item' : 'Create Item'}
      />
    </div>
  );
};

export default ItemCreateEdit; 